import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";

export const Route = createFileRoute("/conversational-analytics")({
  component: ConversationalAnalytics,
});

interface QACard {
  id: string;
  question: string;
  answer: string;
}

const QA_CARDS: QACard[] = [
  {
    id: "product",
    question: "What are my top trade-in products?",
    answer: `Here are your **top 5 trade-in products** by volume this quarter:

1. **Beta AR Jacket** — 142 units, avg value $148
2. **Atom LT Hoody** — 98 units, avg value $72
3. **Alpha SV Jacket** — 67 units, avg value $201
4. **Gamma LT Pant** — 54 units, avg value $53
5. **Zeta SL Jacket** — 41 units, avg value $89

The Beta AR Jacket has the highest resale velocity — items are relisting within 8 days on average.`,
  },
  {
    id: "region",
    question: "Which regions are driving the most trade-ins?",
    answer: `**Regional breakdown** for Q1 2025:

- **Pacific Northwest** — 38% of volume (Vancouver, Seattle, Portland)
- **Northeast US** — 24% (New York, Boston, Montreal)
- **Alps Region** — 19% (Chamonix, Innsbruck, Zermatt)
- **British Columbia** — 12%
- **Other** — 7%

Stores within 2km of a ReBird drop-off point see **4x higher** trade-in conversion rates.`,
  },
  {
    id: "value",
    question: "What's the value and margin on resale?",
    answer: `**Trade-in value analysis:**

Average resale multiple vs trade-in payout is **2.1x** — items sold through ReBird generate $2.10 for every $1 paid out as trade-in credit.

Gross margin on resale items: **34%** after refurbishment costs.

Items graded "Like New" command a **68% premium** over "Good" condition in resale pricing.`,
  },
  {
    id: "trend",
    question: "What trends are emerging in the programme?",
    answer: `**Trending insights from TES event data:**

↑ Insulated jacket submissions are up **41% YoY** — aligns with year-round alpine use cases.

↑ Customers who trade in have a **2.3x higher LTV** than standard retail customers.

↓ Shell jacket resale velocity has slowed slightly — 14 days avg vs 9 days last quarter. Pricing adjustment recommended.

The TES memory layer is tracking **12,400 unique item journeys** since launch.`,
  },
];

function renderAnswer(text: string) {
  return text.split("\n").map((line, j) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((part, k) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={k}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
    return line === "" ? (
      <br key={j} />
    ) : (
      <p key={j} className={j > 0 && line !== "" ? "mt-1.5" : ""}>
        {parts}
      </p>
    );
  });
}

function ConversationalAnalytics() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [visibleId, setVisibleId] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSelect = (id: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (activeId === id) {
      setActiveId(null);
      setVisibleId(null);
      setIsThinking(false);
      return;
    }
    setActiveId(id);
    setVisibleId(null);
    setIsThinking(true);
    timerRef.current = setTimeout(() => {
      setIsThinking(false);
      setVisibleId(id);
    }, 1200);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const activeCard = QA_CARDS.find((c) => c.id === visibleId) ?? null;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
              Conversational Analytics
            </h1>
            <p className="mt-1 text-sm" style={{ color: "#A9A9A9" }}>
              Explore your ReBird programme data in plain English
            </p>
          </div>
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: "rgba(0,251,169,0.1)",
              color: "#00A87A",
              border: "1px solid rgba(0,251,169,0.3)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#00FBA9" }}
            />
            Powered by TES Memory
          </div>
        </div>

        {/* Early access notice */}
        <div
          className="mt-4 p-4 text-sm"
          style={{
            backgroundColor: "rgba(169,169,169,0.06)",
            border: "1px solid rgba(169,169,169,0.15)",
            color: "#A9A9A9",
          }}
        >
          <span style={{ color: "#171717", fontWeight: 500 }}>Early Preview</span>
          {" "}— This feature uses TES event memory to answer questions about your trade-in and resale programme. It's launching as part of our Q2 intelligence layer, building on the same event spine that powers real-time routing and pricing.
        </div>
      </div>

      {/* Question cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {QA_CARDS.map((card) => {
          const isActive = activeId === card.id;
          return (
            <button
              key={card.id}
              onClick={() => handleSelect(card.id)}
              className="text-left p-4 transition-all"
              style={{
                border: isActive
                  ? "1px solid #171717"
                  : "1px solid rgba(169,169,169,0.2)",
                backgroundColor: isActive ? "#171717" : "#FFFFFF",
                color: isActive ? "#FFFFFF" : "#171717",
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: isActive ? "rgba(0,251,169,0.2)" : "#F5F5F5",
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={isActive ? "#00FBA9" : "#737373"}
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <span className="text-sm font-light leading-snug">
                    {card.question}
                  </span>
                </div>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={isActive ? "#00FBA9" : "#A9A9A9"}
                  strokeWidth="2"
                  className="flex-shrink-0 mt-0.5 transition-transform"
                  style={{ transform: isActive ? "rotate(180deg)" : "rotate(0deg)" }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>

      {/* Thinking indicator */}
      {isThinking && (
        <div
          className="p-5 flex items-center gap-3"
          style={{ border: "1px solid rgba(169,169,169,0.15)", backgroundColor: "#FAFAFA" }}
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "#171717" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00FBA9" strokeWidth="2">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 17L12 22L22 17" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 12L12 17L22 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="flex items-center gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: "#A9A9A9",
                  animation: `thinking 1.2s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
          <span className="text-xs" style={{ color: "#A9A9A9" }}>Querying TES memory…</span>
        </div>
      )}

      {/* Answer panel */}
      {activeCard && (
        <div
          className="p-6"
          style={{
            border: "1px solid rgba(169,169,169,0.15)",
            backgroundColor: "#FAFAFA",
          }}
        >
          <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}>
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#171717" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00FBA9" strokeWidth="2">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 17L12 22L22 17" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 12L12 17L22 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-xs font-medium" style={{ color: "#737373" }}>
              ReBird Analytics · TES Memory
            </p>
          </div>
          <div className="text-sm leading-relaxed" style={{ color: "#171717" }}>
            {renderAnswer(activeCard.answer)}
          </div>
        </div>
      )}

      {!activeCard && !isThinking && (
        <div
          className="p-6 text-center"
          style={{
            border: "1px dashed rgba(169,169,169,0.2)",
            backgroundColor: "transparent",
          }}
        >
          <p className="text-sm" style={{ color: "#A9A9A9" }}>
            Select a question above to see insights
          </p>
        </div>
      )}

      <style>{`
        @keyframes thinking {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
